import { format } from "date-fns";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { FilteredObservationResponse } from "../Types/apiResponses";

/**Parse form data and return it as an object*/
export const parseFormData = (
  e: ChangeEvent<HTMLFormElement>
): any => {
  e.preventDefault();
  const myFormData = new FormData(e.target);
  const formDataObj: any = {};
  myFormData.forEach((value, key) => (formDataObj[key] = value));
  return formDataObj;
};

/**Constructs a query out of kv-pairs */
export const createSearchQueryFromPairs = (
  pairs: any
): string => {
  let searchQuery = "?";
  let appended = false;

  Object.keys(pairs).forEach((key) => {
    if (pairs[key] && pairs[key] != -1 && pairs[key] != "") {
      searchQuery += `${key}=${pairs[key]}&`;
      appended = true;
    }
  });

  if (appended) {
    searchQuery = searchQuery.substring(
      0,
      searchQuery.length - 1
    );
  }
  return searchQuery;
};

export const createPairsFromSearchQuery = (
  searchQuery: string
) => {
  const urlParams = new URLSearchParams(searchQuery);
  return Object.fromEntries(urlParams);
};

const tableSortKeyMapping: { [key: string]: string } = {
  systord: "ioc_systord",
  date: "date_from",
  name: "lastname",
  score: "score",
  change: "score_gain_this_year",
  count: "have_seen",
};

export const useTableSort = (
  list: any
): [
  any[],
  (newKey: string) => void,
  Dispatch<SetStateAction<any[]>>
] => {
  const [state, setState] = useState(
    Array.isArray(list) ? list : []
  );
  const [asc, setAscending] = useState(true);
  const [key, setKey] = useState("systord");

  useEffect(() => {
    setState(Array.isArray(list) ? list : []);
  }, [list]);

  const getCompareFunction = (
    newKey: string,
    ascending: boolean
  ) => {
    return (a: any, b: any) => {
      if (
        !a ||
        !b ||
        typeof a !== "object" ||
        typeof b !== "object"
      ) {
        return 0;
      }

      if (!(newKey in a) || !(newKey in b)) {
        return 0;
      }

      if (newKey === "score_gain_this_year") {
        const aValue = Number(a[newKey]) || 0;
        const bValue = Number(b[newKey]) || 0;
        return ascending ? aValue - bValue : bValue - aValue;
      }

      if (newKey === "date_from") {
        if (a[newKey] === null || a[newKey] === undefined) {
          return 1;
        }
        if (b[newKey] === null || b[newKey] === undefined) {
          return -1;
        }

        if (a[newKey] < b[newKey]) {
          return ascending ? 1 : -1;
        } else if (a[newKey] > b[newKey]) {
          return ascending ? -1 : 1;
        }
      } else {
        if (a[newKey] < b[newKey]) {
          return ascending ? -1 : 1;
        } else if (a[newKey] > b[newKey]) {
          return ascending ? 1 : -1;
        }
      }

      return 0;
    };
  };

  const canSort = (items: any[], newKey: string) => {
    return (
      Array.isArray(items) &&
      items.length > 0 &&
      items[0] &&
      typeof items[0] === "object" &&
      Object.keys(items[0]).includes(newKey)
    );
  };

  const sortList = (
    items: any[],
    newKey: string,
    ascending: boolean
  ) => {
    if (!canSort(items, newKey)) {
      return items;
    }

    return [...items].sort(
      getCompareFunction(newKey, ascending)
    );
  };

  const setSortedState: Dispatch<SetStateAction<any[]>> = (
    value
  ) => {
    setState((previousState) => {
      const nextState =
        typeof value === "function"
          ? value(previousState)
          : value;
      const safeNextState = Array.isArray(nextState)
        ? nextState
        : [];
      const mappedKey = tableSortKeyMapping[key];

      return mappedKey
        ? sortList(safeNextState, mappedKey, asc)
        : safeNextState;
    });
  };

  const sortBy = (newKey: string): void => {
    const isAscending = newKey === key ? !asc : true;
    const mappedKey = tableSortKeyMapping[newKey];

    setAscending(isAscending);

    if (mappedKey) {
      setState((previousState) =>
        sortList(previousState, mappedKey, isAscending)
      );
    }

    setKey(newKey);
  };

  return [state, sortBy, setSortedState];
};

export const signOut = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  window.location.href = "/";
};

export const parseDate = (
  parseObject: FilteredObservationResponse | undefined
) => {
  const {
    date_from,
    date_to,
    interval_monthday_from,
    interval_monthday_to,
  } = parseObject || {};

  if (date_to && date_from && date_to !== date_from) {
    return (
      format(new Date(date_from), "dd.MM.yyyy") +
      " - " +
      format(new Date(date_to), "dd.MM.yyyy")
    );
  }
  if (date_to) {
    return format(new Date(date_to), "dd.MM.yyyy");
  }
  if (interval_monthday_from && interval_monthday_to) {
    const interval_string_from =
      interval_monthday_from >= 1000
        ? interval_monthday_from.toString()
        : "0" + interval_monthday_from.toString();
    const interval_string_to =
      interval_monthday_to >= 1000
        ? interval_monthday_to.toString()
        : "0" + interval_monthday_to.toString();
    const formattedFrom =
      interval_string_from.slice(2, 4) +
      "." +
      interval_string_from.slice(0, 2);
    const formattedTo =
      interval_string_to.slice(2, 4) +
      "." +
      interval_string_to.slice(0, 2);
    return formattedFrom + " - " + formattedTo;
  }
  return "0.0.0000";
};

/** Hardcoded because timetables*/
export const getAreaFromContinent = (id: number) => {
  const CONTINENTS_TO_STATE = [
    {
      id: 1,
      area: 3,
    },
    {
      id: 2,
      area: 3,
    },
    {
      id: 3,
      area: 2,
    },
    {
      id: 4,
      area: 2,
    },
    {
      id: 5,
      area: 2,
    },
    {
      id: 6,
      area: 1,
    },
    {
      id: 7,
      area: 2,
    },
    {
      id: 8,
      area: 1,
    },
  ];

  let result = 3;
  CONTINENTS_TO_STATE.forEach((continent) => {
    if (continent.id === id) result = continent.area;
  });
  /** default to world */
  return result;
};
